import styled from '@emotion/styled';
import React, { useMemo, useState, useRef } from 'react';

import P2 from '@base/P2/P2';
import { Spacings } from '@styles/index';
import { IGroupUser, GroupsStatus } from 'types/groups';
import isUndefined from 'lodash/isUndefined';
import isEqual from 'lodash/isEqual';
import GroupsUserProgress from '../CommonCards/GroupsUserProgress';
import GroupsProfileTrigger from '../CommonCards/groupsProfile/GroupsProfileTrigger';
import {
  capitalize,
  keyFrameClose,
  keyFrameOpen,
  minUsersToDisplay
} from '../groupsUtils';
import GroupProfilePopup, {
  IGroupProfilePopupFn
} from '../CommonCards/groupsProfile/GroupProfilePopup';
import { ActivityObjectTypeInfo } from '../groupsConstants';
import ShowMoreWrapper from '../CommonCards/ShowMoreWrapper';

interface IGroupStatusBlockProps {
  usersList: IGroupUser[];
  currentUserId: string;
  goalUID: string;
  closeDrawer: () => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${Spacings.SPACING_6B};
`;

const UserInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_6};
`;

const StyP2 = styled(P2)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
  width: 90px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
`;

const InnerWrapper = styled.div`
  animation-duration: 300ms;
  &.GroupMembersStatus--show {
    opacity: 1;
    display: flex;
    flex-wrap: wrap;
    animation-name: ${keyFrameOpen};
  }
  &.GroupMembersStatus--hide {
    opacity: 0;
    display: none;
    animation-name: ${keyFrameClose};
  }
`;

const GroupStatusBlock: React.FC<IGroupStatusBlockProps> = ({
  usersList,
  currentUserId,
  goalUID,
  closeDrawer
}) => {
  const [showAllUsers, setShowAllUsers] = useState(false);
  const popupRef = useRef<IGroupProfilePopupFn>();

  const membersList = useMemo(
    () => usersList.slice(0, minUsersToDisplay),
    [usersList]
  );
  const hiddenMembersList = useMemo(
    () => usersList.slice(minUsersToDisplay, usersList.length),
    [usersList]
  );

  const handleShowPopup = (targetUser) => (event) => {
    popupRef.current?.handleShowPopup(event, targetUser);
  };

  const handleClosePopup = () => {
    popupRef.current?.handleHidePopup();
  };

  const renderActivity = ({ status = '', latestActivity, uid }) => {
    const hasLatestActivity = latestActivity && !isEqual(latestActivity, {});
    const activityType = hasLatestActivity
      ? latestActivity.object_type
      : undefined;

    let activity = '';

    if (
      status === GroupsStatus.OFFLINE ||
      isUndefined(activityType) ||
      uid === currentUserId
    ) {
      activity = capitalize(status);
    } else {
      activity = ActivityObjectTypeInfo[activityType].activity;
    }

    return activity;
  };

  return (
    <Wrapper>
      <GroupProfilePopup
        goalUID={goalUID}
        ref={popupRef}
        closeDrawer={closeDrawer}
        direction="top"
      >
        <>
          {membersList.map((user) => (
            <UserInfoWrapper key={user.uid}>
              <GroupsProfileTrigger
                onClick={handleShowPopup(user)}
                onHidePopup={handleClosePopup}
              >
                <GroupsUserProgress
                  user={user}
                  avatarSize={Spacings.SPACING_10B}
                  outerProgressSize={Spacings.SPACING_18B}
                  innerProgressSize={Spacings.SPACING_14B}
                  outerDashOffset={170}
                  innerDashOffset={130}
                  type="drawer"
                  name={user.uid === currentUserId ? 'Me' : user.firstName}
                />
              </GroupsProfileTrigger>
              {user.status && <StyP2>{renderActivity(user)}</StyP2>}
            </UserInfoWrapper>
          ))}
          <InnerWrapper
            className={
              showAllUsers
                ? 'GroupMembersStatus--show'
                : 'GroupMembersStatus--hide'
            }
          >
            {hiddenMembersList.map((user) => (
              <UserInfoWrapper key={user.uid}>
                <GroupsProfileTrigger
                  onClick={handleShowPopup(user)}
                  onHidePopup={handleClosePopup}
                >
                  <GroupsUserProgress
                    user={user}
                    avatarSize={Spacings.SPACING_10B}
                    outerProgressSize={Spacings.SPACING_18B}
                    innerProgressSize={Spacings.SPACING_14B}
                    outerDashOffset={170}
                    innerDashOffset={130}
                    type="drawer"
                    name={user.uid === currentUserId ? 'Me' : user.firstName}
                  />
                </GroupsProfileTrigger>
                {user.status && <StyP2>{renderActivity(user)}</StyP2>}
              </UserInfoWrapper>
            ))}
          </InnerWrapper>
        </>
      </GroupProfilePopup>
      {usersList.length > minUsersToDisplay && (
        <ShowMoreWrapper
          showAllUsers={showAllUsers}
          onClickShowButton={() => {
            setShowAllUsers(!showAllUsers);
          }}
        />
      )}
    </Wrapper>
  );
};

export default GroupStatusBlock;
